// Single source of truth for turning a job's stored location object into the display string
// shown on cards, badges and detail pages. Historically every renderer inlined
// `[city, state].join(', ')` (dropping the country) and, worse, several used the ABSENCE of
// city/state as a stand-in for "Remote" — so an international role (Dublin/Ireland, whose region
// equals its city -> "Dublin, Dublin") lost its country, and a location-less row falsely read as
// Remote. This helper fixes both: it keys Remote off the isRemote FLAG and surfaces the country
// for international locations.

const US_COUNTRY_NAMES = new Set([
	'us',
	'usa',
	'u.s.',
	'u.s.a.',
	'united states',
	'united states of america',
]);

// The geocoder stamps ISO 3166 "official" country names, a few of which carry a
// trailing " (the)" ("Bahamas (the)", "United States of America (the)"). Strip it
// before any compare or display so the noise never reaches a badge.
const stripThe = (name) => name.replace(/\s*\(the\)\s*$/i, '').trim();

export const isUSCountry = (country) =>
	typeof country === 'string' && US_COUNTRY_NAMES.has(stripThe(country.trim().toLowerCase()));

// A few official names are too verbose for a location badge; map them to the
// short form people actually use. Keyed by ISO-3/2 code OR the full name; every
// other country shows its ("(the)"-stripped) name as-is (Ireland, France, …).
const COUNTRY_DISPLAY_OVERRIDES = new Map([
	['gbr', 'UK'],
	['gb', 'UK'],
	['uk', 'UK'],
	['united kingdom of great britain and northern ireland', 'UK'],
	['united kingdom', 'UK'],
]);

// The country string to SHOW: an override when one applies, else the name with
// its "(the)" trimmed, else the raw country code.
const displayCountry = (countryName, country) => {
	const named = stripThe((countryName || '').trim());
	return (
		COUNTRY_DISPLAY_OVERRIDES.get(named.toLowerCase()) ||
		COUNTRY_DISPLAY_OVERRIDES.get((country || '').trim().toLowerCase()) ||
		named ||
		(country || '').trim()
	);
};

/**
 * Format a job location object into its display string.
 *
 * Rules, in order:
 *   - prefersOriginalLocation + originalLocation -> the original string verbatim.
 *   - isRemote (and not preferring original) -> the remote label.
 *   - a KNOWN non-US country -> "City, Country" (the region is dropped: for many international
 *     places it duplicates the city — "Dublin, Dublin" — or is noise, and the country is what
 *     recruiters filter/search by).
 *   - otherwise (US, or no country captured) -> "City, State" (the familiar domestic convention,
 *     and the safe default that preserves legacy {city, state} rows).
 *   - '' when there is nothing to show.
 *
 * @param {object|null|undefined} location {city,state,country,countryName,isRemote,originalLocation,prefersOriginalLocation}
 * @param {string} remoteLabel localized "Remote" label (pass ml('Remote', ...); defaults to 'Remote')
 * @returns {string}
 */
export const formatJobLocation = (location, remoteLabel = 'Remote') => {
	if (!location) return '';
	const { city, state, country, countryName, isRemote, originalLocation } = location;
	const prefersOriginalLocation =
		location.prefersOriginalLocation === 'true' || location.prefersOriginalLocation === true;

	if (prefersOriginalLocation && originalLocation) return originalLocation;
	if (isRemote && !prefersOriginalLocation) return remoteLabel;

	const rawCountry = countryName || country;
	const shownCountry = displayCountry(countryName, country);
	const parts =
		rawCountry && !isUSCountry(rawCountry) ? [city, shownCountry] : [city, state];
	const joined = parts.filter(Boolean).join(', ');
	// Fallback for odd shapes (e.g. an international row whose country isn't captured yet).
	return joined || [city, state, shownCountry].filter(Boolean).join(', ');
};
